// src/components/Navbar.jsx
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { eErroreDiRete } from '../utils/rete';
import { useEffect, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import EnableNotificationsButton from './EnableNotificationsButton';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [previewImage, setPreviewImage] = useState('/default-avatar.jpg');
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = '/api';

  const storedRole = localStorage.getItem('role');
  const isClientUser = storedRole === 'cliente';

  const CLIENT_AREA_PATHS = [
    '/dashboard-cliente',
    '/cliente-macchine',
  ];

  const CLIENT_SHARED_PATHS = [
    '/supporto',
    '/profilo',
  ];

  const matchesPath = (paths) =>
    paths.some(
      (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`)
    );

  const isClientArea =
    matchesPath(CLIENT_AREA_PATHS) ||
    (isClientUser && matchesPath(CLIENT_SHARED_PATHS));

  const navLinkClass = (path) => {
    const active = location.pathname === path;

    if (isClientArea) {
      return `px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap border ${
        active
          ? 'bg-white/10 text-white border-cyan-300/25'
          : 'text-slate-300 hover:text-white hover:bg-white/[0.06] border-transparent'
      }`;
    }

    return `px-4 py-2 rounded-lg text-sm font-semibold transition ${
      active
        ? 'bg-blue-600 text-white'
        : 'text-gray-300 hover:text-white hover:bg-blue-600/90'
    }`;
  };

const actionButtonClass = isClientArea
  ? 'px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap text-slate-300 hover:text-white hover:bg-white/[0.06] border border-transparent'
  : 'px-4 py-2 rounded-lg text-sm font-semibold transition text-gray-300 hover:text-white hover:bg-blue-600/90';

    const fetchUserData = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/user`);

      if (res.ok) {
        const data = await res.json();

        setIsLoggedIn(true);
        setPreviewImage(
          data.profilePicture
            ? `${BACKEND_URL}${data.profilePicture}`
            : '/default-avatar.jpg'
        );
      } else {
        if (res.status === 401) {
          logout();
        } else {
          setIsLoggedIn(false);
          setPreviewImage('/default-avatar.jpg');
        }
      }
    } catch (e) {
      console.error('Errore fetch profilo:', e);
      // la Navbar sta su tutte le pagine: un errore di rete qui
      // sloggava il tecnico ovunque si trovasse
      if (!eErroreDiRete(e)) logout();
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('mustChangePassword');

    setIsLoggedIn(false);
    setPreviewImage('/default-avatar.jpg');
    setMenuOpen(false);

    navigate('/login');
  };

  useEffect(() => {
    setMenuOpen(false);
    setLoading(true);

    const token = localStorage.getItem('token');

    if (token) {
      fetchUserData();
    } else {
      setIsLoggedIn(false);
      setPreviewImage('/default-avatar.jpg');
      setLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (loading) return null;

  return (
    <>
      <header
        className={
          isClientArea
            ? 'sticky top-0 z-50 bg-slate-950/95 backdrop-blur supports-[backdrop-filter]:bg-slate-950/88 border-b border-white/10 shadow-sm'
            : 'sticky top-0 z-50 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/80 border-b border-gray-800'
        }
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="h-16 flex items-center gap-4">
          {/* BRAND */}
          <Link
            to={isClientArea ? '/dashboard-cliente' : '/'}
            className="flex items-center gap-3 min-w-0 shrink"
          >
            {isClientArea ? (
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <img
                  src="/logo-removebg.png"
                  alt="Ciesse Impianti"
                  className="h-8 sm:h-9 max-w-[72px] sm:max-w-none w-auto object-contain shrink-0"
                />

                <div className="hidden md:flex flex-col leading-none min-w-0">
                  <span className="text-white font-bold text-sm tracking-wide truncate">
                    CIESSE
                  </span>
                  <span className="text-slate-300 text-[11px] font-semibold truncate">
                    Impianti
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center shrink-0 px-0.5">
                <span className="text-slate-400 text-sm sm:text-base font-semibold leading-none">
                  ×
                </span>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <img
                  src="/logo.png"
                  alt="Clotho"
                  className="h-8 sm:h-10 max-w-[44px] sm:max-w-none w-auto object-contain shrink-0"
                />

                <div className="hidden md:flex flex-col leading-none min-w-0">
                  <span className="text-white font-bold text-sm tracking-wide truncate">
                    Clotho
                  </span>
                  <span className="text-cyan-200/80 text-[11px] font-semibold truncate">
                    IoT
                  </span>
                </div>
              </div>

              <div className="flex md:hidden flex-col leading-none min-w-0">
                <span className="text-white font-bold text-[12px] tracking-wide truncate">
                  CIESSE × Clotho
                </span>

              </div>
            </div>
            ) : (
              <>
                <img
                  src="/logo-removebg.png"
                  alt="Logo"
                  className="h-9 w-auto"
                />

                <span className="text-white font-bold text-lg leading-none truncate">
                  Ciesse{' '}
                  <span className="text-gray-300 font-semibold">
                    Impianti
                  </span>
                </span>
              </>
            )}
          </Link>

          {/* NAV DESKTOP */}
          <nav className="hidden md:flex items-center justify-end gap-2 ml-auto shrink-0">
            {isClientArea && isLoggedIn ? (
              <>
                <Link
                  to="/dashboard-cliente"
                  className={navLinkClass('/dashboard-cliente')}
                >
                  Area cliente
                </Link>

                <Link
                  to="/cliente-macchine"
                  className={navLinkClass('/cliente-macchine')}
                >
                  Macchine
                </Link>

                <Link to="/supporto" className={navLinkClass('/supporto')}>
                  Supporto
                </Link>

                <EnableNotificationsButton compact />

                <button
                  type="button"
                  onClick={logout}
                  className={actionButtonClass}
                >
                  Logout
                </button>

                <Link to="/profilo" className="ml-1">
                  <div className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-white/20 hover:ring-cyan-300/60 transition">
                    <img
                      src={previewImage}
                      alt="Profilo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/default-avatar.jpg';
                      }}
                    />
                  </div>
                </Link>
              </>
            ) : (
              <>
                {!isLoggedIn && (
                  <Link to="/" className={navLinkClass('/')}>
                    Home
                  </Link>
                )}

                <Link to="/supporto" className={navLinkClass('/supporto')}>
                  Supporto
                </Link>

                {!isLoggedIn ? (
                  <Link to="/login" className={navLinkClass('/login')}>
                    Login
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/dashboard"
                      className={navLinkClass('/dashboard')}
                    >
                      Dashboard
                    </Link>

                    <EnableNotificationsButton compact />

                    <button
                      type="button"
                      onClick={logout}
                      className={actionButtonClass}
                    >
                      Logout
                    </button>

                    <Link to="/profilo" className="ml-2">
                      <div className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-blue-500/70">
                        <img
                          src={previewImage}
                          alt="Profilo"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = '/default-avatar.jpg';
                          }}
                        />
                      </div>
                    </Link>
                  </>
                )}
              </>
            )}
          </nav>

            {/* HAMBURGER MOBILE */}
            <button
              type="button"
              className={`md:hidden ml-auto inline-flex items-center justify-center h-10 w-10 rounded-lg transition ${
                isClientArea
                  ? 'text-cyan-100 hover:text-white hover:bg-white/10'
                  : 'text-gray-200 hover:text-white hover:bg-blue-600/20'
              }`}              
              aria-label="Apri menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d={
                    menuOpen
                      ? 'M6 6l12 12M18 6l-12 12'
                      : 'M4 7h16M4 12h16M4 17h16'
                  }
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* PANEL MOBILE */}
        {menuOpen && (
          <div className="md:hidden relative z-50">
            <div
              className={`mx-4 mb-3 rounded-2xl border shadow-xl overflow-hidden ${
                isClientArea
                  ? 'border-cyan-300/20 bg-[#090017]/95 shadow-[0_18px_50px_rgba(34,211,238,0.16)]'
                  : 'border-gray-800 bg-gray-900/95'
              }`}
            >
              <div className="p-2 flex flex-col">
                {isClientArea && isLoggedIn ? (
                  <>
                  <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/10 mb-2">
                    <div className="flex items-center gap-2">
                      <img
                        src="/logo-removebg.png"
                        alt="Ciesse Impianti"
                        className="h-8 max-w-[78px] w-auto object-contain"
                      />

                      <span className="text-slate-400 text-sm font-semibold">
                        ×
                      </span>

                      <img
                        src="/logo.png"
                        alt="Clotho"
                        className="h-8 w-auto object-contain"
                      />

                      <div className="ml-1 flex flex-col leading-none min-w-0">
                        <span className="text-white text-xs font-bold truncate">
                          CIESSE × Clotho
                        </span>

                      </div>
                    </div>
                  </div>

                    <Link
                      to="/dashboard-cliente"
                      className={navLinkClass('/dashboard-cliente')}
                      onClick={() => setMenuOpen(false)}
                    >
                      Area cliente
                    </Link>

                    <Link
                      to="/cliente-macchine"
                      className={navLinkClass('/cliente-macchine')}
                      onClick={() => setMenuOpen(false)}
                    >
                      Macchine
                    </Link>

                    <Link
                      to="/supporto"
                      className={navLinkClass('/supporto')}
                      onClick={() => setMenuOpen(false)}
                    >
                      Supporto
                    </Link>

                    <div className="mt-2 px-2">
                      <EnableNotificationsButton
                        compact
                        onDone={() => setMenuOpen(false)}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={logout}
                      className="mt-2 text-left px-4 py-2 rounded-xl text-sm font-semibold text-cyan-50/80 hover:text-white hover:bg-white/10 transition"
                    >
                      Logout
                    </button>

                    <Link
                      to="/profilo"
                      className="mt-2 flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/10"
                      onClick={() => setMenuOpen(false)}
                    >
                      <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-cyan-300/80">
                        <img
                          src={previewImage}
                          alt="Profilo"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = '/default-avatar.jpg';
                          }}
                        />
                      </div>

                      <div className="flex flex-col">
                        <span className="text-white text-sm font-semibold">
                          Profilo
                        </span>
                        <span className="text-cyan-100/60 text-xs">
                          Impostazioni account
                        </span>
                      </div>
                    </Link>
                  </>
                ) : (
                  <>
                    {!isLoggedIn && (
                      <Link
                        to="/"
                        className={navLinkClass('/')}
                        onClick={() => setMenuOpen(false)}
                      >
                        Home
                      </Link>
                    )}

                    <Link
                      to="/supporto"
                      className={navLinkClass('/supporto')}
                      onClick={() => setMenuOpen(false)}
                    >
                      Supporto
                    </Link>

                    {!isLoggedIn ? (
                      <Link
                        to="/login"
                        className={navLinkClass('/login')}
                        onClick={() => setMenuOpen(false)}
                      >
                        Login
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className={navLinkClass('/dashboard')}
                          onClick={() => setMenuOpen(false)}
                        >
                          Dashboard
                        </Link>

                        <div className="mt-2 px-2">
                          <EnableNotificationsButton
                            compact
                            onDone={() => setMenuOpen(false)}
                          />
                        </div>

                        <button
                          type="button"
                          onClick={logout}
                          className="mt-2 text-left px-4 py-2 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:bg-blue-600/90 transition"
                        >
                          Logout
                        </button>

                        <Link
                          to="/profilo"
                          className="mt-2 flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-600/10"
                          onClick={() => setMenuOpen(false)}
                        >
                          <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-blue-500/70">
                            <img
                              src={previewImage}
                              alt="Profilo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = '/default-avatar.jpg';
                              }}
                            />
                          </div>

                          <div className="flex flex-col">
                            <span className="text-white text-sm font-semibold">
                              Profilo
                            </span>
                            <span className="text-gray-400 text-xs">
                              Impostazioni account
                            </span>
                          </div>
                        </Link>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* OVERLAY MOBILE */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}